import { Component, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BalanceType } from '../../../../../shared/schemas/balanceSchema';
import { OrderType } from '../../../../../shared/schemas/orderSchema';
import { UserType, UserTypeWithOrders } from '../../../../../shared/schemas/userSchema';
import { addUserToStore } from '../../store/slices/usersSlice/usersSlice.actions';
import {
  selectAllUsers,
  selectCurrentUser,
} from '../../store/slices/usersSlice/usersSlice.selector';
import { AppStore } from '../../store/store';
import { UsersService } from '../prayers/services/users.service';
import { BalanceService } from '../synagogue/services/balance.service';
import labels from './data/home.json';

export type BtnsData = {
  txt: string;
  icon: string;
  link: string;
};

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
})
export class HomeComponent implements OnInit, OnDestroy {
  currentUser$: Observable<UserType | null> = this.store.select(selectCurrentUser);
  users$: Observable<UserTypeWithOrders[]> = this.store.select(selectAllUsers);
  // Derived stats, updated whenever users$ emits.
  totalUsers = 0;
  negativeBalanceCount = 0;
  totalDebt = 0;
  // Stats show a skeleton until THIS visit's fetch lands — never a stale
  // number left over in the store from an earlier visit.
  usersLoaded = false;
  usersError = false;
  // Synagogue balance: null = still loading.
  balanceDisplay: number | null = null;
  lastTxnAmount = 0;
  balanceError = false;
  user: UserType | null = null;
  latestOrders: OrderType[] = [];
  quickActions: BtnsData[];
  actions: BtnsData[];
  dateVal: Date = new Date();
  allActionsHeader = labels.allActions;
  quickActionsHeader = labels.quickAction;
  header = labels.header;
  welcome = labels.welcome;

  private destroy$ = new Subject<void>();

  constructor(
    private store: Store<AppStore>,
    private userService: UsersService,
    private balanceService: BalanceService,
  ) {
    this.quickActions = [
      { txt: labels.addOrder, icon: 'add_circle', link: '/prayers/order/add' },
      { txt: labels.bigTable, icon: 'table_view', link: '/prayers/overall-table' },
      { txt: labels.customForms, icon: 'photo', link: '/prayers/custom-prints' },
    ];

    this.actions = [
      { txt: labels.prayers, icon: 'group', link: '/prayers-panel' },
      { txt: labels.synagogue, icon: 'synagogue', link: '/synagogue-panel' },
      { txt: labels.doners, icon: 'volunteer_activism', link: '/doners' },
    ];
  }

  ngOnInit(): void {
    // Always refetch on mount. The store's `addUserToStore` reducer upserts
    // so re-dispatching the same user is harmless and keeps the cache fresh.
    // The old "if (users.length === 0) fetch" guard was broken: after the
    // session restore the store already had one user (the admin), so the
    // fetch never ran and pages showed only the admin.
    this.fetchUsers();
    this.fetchBalance();

    // Bind currentUser → template (for the greeting).
    this.store
      .pipe(select(selectCurrentUser), takeUntil(this.destroy$))
      .subscribe((user) => (this.user = user));

    // Recompute stats whenever the user list changes (initial empty, then
    // populated, then on any subsequent mutation).
    this.store
      .pipe(select(selectAllUsers), takeUntil(this.destroy$))
      .subscribe((users) => this.updateUserStats(users));
  }

  private fetchUsers(): void {
    this.userService
      .getUsers()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (fetched) => {
          fetched.forEach((user) => this.store.dispatch(addUserToStore({ user })));
          this.usersLoaded = true;
        },
        error: (err) => {
          this.usersError = true;
          console.error('Error fetching users:', err);
        },
      });
  }

  /**
   * Always straight from the server (never the cached store value), so a
   * transaction made a moment ago is guaranteed to be in the total.
   * `refreshLatest` also pushes the row into the store for everyone else.
   */
  private async fetchBalance(): Promise<void> {
    try {
      const latest = await this.balanceService.refreshLatest();
      this.updateBalanceStats(latest);
    } catch (error) {
      this.balanceError = true;
      console.error('Error fetching balance:', error);
    }
  }

  private updateUserStats(users: readonly UserTypeWithOrders[]): void {
    this.totalUsers = users.length;
    const debtors = users.filter((u) => (u.balance ?? 0) < 0);
    this.negativeBalanceCount = debtors.length;
    this.totalDebt = debtors.reduce((sum, u) => sum + (u.balance ?? 0), 0);
  }

  private updateBalanceStats(latest: BalanceType | null): void {
    // No balance rows yet (no transactions ever) → the balance is 0.
    this.balanceDisplay = latest?.balance ?? 0;
    // Signed impact of the newest transaction: + income, − expense.
    this.lastTxnAmount = latest?.transactionAmount ?? 0;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
